import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Download, Loader2, AlertTriangle, Check, X, Edit, Trash2, ChevronDown, ChevronRight,
  Users, MessageSquare, Bell, TrendingUp, TrendingDown, Wallet, Plus,
} from "lucide-react";
import { toast } from "sonner";

const fmtTime = (iso) => iso ? new Date(iso).toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit", second: "2-digit" }) : "—";
// Fecha local Europe/Madrid (YYYY-MM-DD) del timestamp — para detectar jornadas que cruzan medianoche
const localDate = (iso) => {
  if (!iso) return null;
  const parts = new Intl.DateTimeFormat("en-CA", { timeZone: "Europe/Madrid", year: "numeric", month: "2-digit", day: "2-digit" })
    .formatToParts(new Date(iso)).reduce((a, p) => ({ ...a, [p.type]: p.value }), {});
  return `${parts.year}-${parts.month}-${parts.day}`;
};
const cruzaMedianoche = (f) => f && f.fecha && localDate(f.timestamp) !== f.fecha;
const fmtH = (v) => `${v > 0 ? "+" : ""}${(v ?? 0).toFixed(1)} h`;
const MESES_ES = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
                  "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];

export default function FichajesAdmin() {
  const { hasRole } = useAuth();
  const isReadOnly = !hasRole("admin"); // gestoria es read-only
  const [fichajes, setFichajes] = useState([]);
  const [discrepancias, setDiscrepancias] = useState(null);
  const [correcciones, setCorrecciones] = useState([]);
  const [users, setUsers] = useState([]);
  const [resumen, setResumen] = useState(null);
  const [resumenMes, setResumenMes] = useState({
    year: new Date().getFullYear(),
    month: new Date().getMonth() + 1,
  });
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState("fichajes"); // fichajes | resumen | discrepancias | correcciones | banco
  const [filtros, setFiltros] = useState({
    user_id: "",
    desde: new Date(Date.now() - 30 * 86400000).toISOString().slice(0, 10),
    hasta: new Date().toISOString().slice(0, 10),
  });
  const [edit, setEdit] = useState(null);
  const [editForm, setEditForm] = useState({ timestamp: "", tipo: "", notas: "", edit_reason: "" });
  // Iter 94 — rescate entrada huérfana
  const [rescatarEntrada, setRescatarEntrada] = useState(null);
  const [rescateForm, setRescateForm] = useState({ fecha: "", hora: "", motivo: "" });
  const [rescatando, setRescatando] = useState(false);

  const ejecutarRescate = async () => {
    if (!rescatarEntrada) return;
    if (!rescateForm.fecha || !rescateForm.hora) {
      toast.error("Fecha y hora de salida son obligatorias");
      return;
    }
    setRescatando(true);
    try {
      // Combinar fecha + hora locales y enviar ISO
      const isoLocal = new Date(`${rescateForm.fecha}T${rescateForm.hora}:00`).toISOString();
      await api.post("/fichajes/cerrar-entrada-huerfana", {
        user_id: rescatarEntrada.user_id,
        timestamp_salida: isoLocal,
        motivo: rescateForm.motivo || `Cierre manual jornada ${rescatarEntrada.fecha}`,
      });
      toast.success(`Jornada de ${rescatarEntrada.user_nombre} cerrada.`);
      setRescatarEntrada(null);
      setRescateForm({ fecha: "", hora: "", motivo: "" });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al cerrar jornada");
    } finally { setRescatando(false); }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filtros.user_id) params.user_id = filtros.user_id;
      if (filtros.desde) params.desde = filtros.desde;
      if (filtros.hasta) params.hasta = filtros.hasta;
      const [f, d, c, u] = await Promise.all([
        api.get("/fichajes", { params }),
        api.get("/fichajes/discrepancias"),
        api.get("/fichajes/correcciones"),
        api.get("/users"),
      ]);
      setFichajes(f.data || []);
      setDiscrepancias(d.data);
      setCorrecciones(c.data || []);
      setUsers(u.data || []);
    } catch (err) {
      toast.error("Error cargando datos");
    } finally { setLoading(false); }
  }, [filtros]);
  useEffect(() => { load(); }, [load]);

  const loadResumen = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/fichajes/resumen-mensual", { params: resumenMes });
      setResumen(data);
    } catch (err) {
      toast.error("Error cargando resumen");
    } finally { setLoading(false); }
  }, [resumenMes]);
  useEffect(() => { if (tab === "resumen") loadResumen(); }, [tab, loadResumen]);

  const exportResumenCSV = async () => {
    try {
      const url = `/fichajes/resumen-mensual.csv?year=${resumenMes.year}&month=${resumenMes.month}`;
      const { data } = await api.get(url, { responseType: "blob" });
      const blob = new Blob([data], { type: "text/csv;charset=utf-8" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `resumen_horas_${resumenMes.year}-${String(resumenMes.month).padStart(2, "0")}.csv`;
      a.click();
    } catch (err) {
      toast.error("Error exportando resumen");
    }
  };

  const exportCSV = async () => {
    try {
      const params = new URLSearchParams();
      if (filtros.user_id) params.set("user_id", filtros.user_id);
      if (filtros.desde) params.set("desde", filtros.desde);
      if (filtros.hasta) params.set("hasta", filtros.hasta);
      const url = `/fichajes/export.csv?${params.toString()}`;
      const { data } = await api.get(url, { responseType: "blob" });
      const blob = new Blob([data], { type: "text/csv;charset=utf-8" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `fichajes_${filtros.desde}_${filtros.hasta}.csv`;
      a.click();
    } catch (err) {
      toast.error("Error exportando");
    }
  };

  const openEdit = (f) => {
    setEdit(f);
    setEditForm({
      timestamp: f.timestamp.slice(0, 16),
      tipo: f.tipo,
      notas: f.notas || "",
      trabajo_realizado: f.trabajo_realizado || "",
      edit_reason: "",
    });
  };
  const saveEdit = async () => {
    if (!editForm.edit_reason.trim()) return toast.error("El motivo es obligatorio");
    try {
      await api.patch(`/fichajes/${edit.id}`, {
        timestamp: new Date(editForm.timestamp).toISOString(),
        tipo: editForm.tipo,
        notas: editForm.notas,
        trabajo_realizado: editForm.trabajo_realizado,
        edit_reason: editForm.edit_reason,
      });
      toast.success("Fichaje actualizado");
      setEdit(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    }
  };
  const remove = async (f) => {
    if (!window.confirm("¿Eliminar fichaje? Quedará en auditoría.")) return;
    try {
      await api.delete(`/fichajes/${f.id}`);
      toast.success("Eliminado");
      load();
    } catch (err) { toast.error("Error"); }
  };

  const resolverCorr = async (c, aprobar) => {
    const comentario = window.prompt(aprobar ? "Comentario (opcional):" : "Motivo de rechazo:");
    try {
      await api.patch(`/fichajes/correcciones/${c.id}`, { aprobar, comentario });
      toast.success(aprobar ? "Aprobada y aplicada" : "Rechazada");
      load();
    } catch (err) { toast.error("Error"); }
  };

  const triggerRecordatorio = async (modo) => {
    try {
      await api.post(`/fichajes/recordatorios/trigger?modo=${modo}`);
      toast.success(`📱 Recordatorios de ${modo} disparados`);
    } catch (err) { toast.error("Error"); }
  };

  const correccionesPendientes = correcciones.filter((c) => c.estado === "pendiente");

  return (
    <div className="p-6" data-testid="fichajes-admin-page">
      <PageHeader title="Fichajes · Administración"
        subtitle="Auditoría · discrepancias · correcciones · exportación legal"
        action={
          <div className="flex gap-2">
            {!isReadOnly && (
              <>
                <Button variant="outline" onClick={() => triggerRecordatorio("entrada")}
                  className="bg-transparent border-zinc-700 text-zinc-300 text-xs" data-testid="trigger-entrada">
                  <Bell className="w-3.5 h-3.5 mr-1" /> Recordar entrada
                </Button>
                <Button variant="outline" onClick={() => triggerRecordatorio("salida")}
                  className="bg-transparent border-zinc-700 text-zinc-300 text-xs" data-testid="trigger-salida">
                  <Bell className="w-3.5 h-3.5 mr-1" /> Recordar salida
                </Button>
              </>
            )}
            <Button onClick={exportCSV}
              className="bg-amber-500 text-zinc-950 hover:bg-amber-400" data-testid="btn-export">
              <Download className="w-4 h-4 mr-2" /> Exportar CSV
            </Button>
          </div>
        }
      />

      {/* Tabs */}
      <div className="flex gap-2 mb-4 border-b border-zinc-800">
        {[
          ["fichajes", "Fichajes", fichajes.length],
          ["resumen", "Resumen mensual", 0],
          ["banco", "Banco horas", 0],
          ["discrepancias", "Discrepancias", discrepancias?.total || 0],
          ["correcciones", "Correcciones", correccionesPendientes.length],
        ].map(([k, l, n]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`tab-${k}`}
            className={`px-4 py-2 text-sm border-b-2 transition ${tab === k ? "border-amber-500 text-amber-300" : "border-transparent text-zinc-400 hover:text-zinc-100"}`}>
            {l} {n > 0 && <span className="ml-1 bg-zinc-800 text-zinc-300 rounded-full px-1.5 py-0.5 text-[10px]">{n}</span>}
          </button>
        ))}
      </div>

      {/* Filtros */}
      {tab === "fichajes" && (
        <div className="bg-zinc-900/40 border border-zinc-800/70 rounded-lg p-3 mb-4 flex flex-wrap gap-3 items-end">
          <div>
            <Label className="text-xs text-zinc-400">Empleado</Label>
            <Select value={filtros.user_id || "__ALL__"} onValueChange={(v) => setFiltros({ ...filtros, user_id: v === "__ALL__" ? "" : v })}>
              <SelectTrigger className="bg-zinc-950 border-zinc-800 w-48" data-testid="filter-user"><SelectValue placeholder="Todos" /></SelectTrigger>
              <SelectContent className="bg-zinc-950 border-zinc-800 text-zinc-100 max-h-72">
                <SelectItem value="__ALL__">Todos</SelectItem>
                {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs text-zinc-400">Desde</Label>
            <Input type="date" value={filtros.desde} onChange={(e) => setFiltros({ ...filtros, desde: e.target.value })}
              className="bg-zinc-950 border-zinc-800" data-testid="filter-desde" />
          </div>
          <div>
            <Label className="text-xs text-zinc-400">Hasta</Label>
            <Input type="date" value={filtros.hasta} onChange={(e) => setFiltros({ ...filtros, hasta: e.target.value })}
              className="bg-zinc-950 border-zinc-800" data-testid="filter-hasta" />
          </div>
        </div>
      )}

      {/* Contenido tabs */}
      {loading ? (
        <div className="flex items-center justify-center py-20 text-zinc-500">
          <Loader2 className="w-6 h-6 animate-spin mr-2" /> Cargando…
        </div>
      ) : tab === "fichajes" ? (
        <div className="bg-zinc-900/30 border border-zinc-800/60 rounded-lg overflow-hidden" data-testid="fichajes-tabla">
          {fichajes.length === 0 ? (
            <div className="text-center py-12 text-zinc-500">No hay fichajes con esos filtros</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-zinc-950/60 text-[10px] uppercase tracking-wider text-zinc-500 border-b border-zinc-800">
                <tr>
                  <th className="text-left p-2.5">Empleado</th>
                  <th className="text-left p-2.5">Fecha laboral</th>
                  <th className="text-left p-2.5">Hora</th>
                  <th className="text-left p-2.5">Tipo</th>
                  <th className="text-left p-2.5">Trabajo</th>
                  <th className="text-left p-2.5">IP / GPS</th>
                  <th className="p-2.5"></th>
                </tr>
              </thead>
              <tbody>
                {fichajes.map((f) => (
                  <tr key={f.id} className="border-b border-zinc-800/60 hover:bg-zinc-900/40"
                    data-testid={`row-${f.id}`}>
                    <td className="p-2.5 text-zinc-100">{f.user_nombre}</td>
                    <td className="p-2.5 text-zinc-400 text-xs">
                      {f.fecha}
                      {cruzaMedianoche(f) && (
                        <span className="ml-1.5 text-[9px] px-1.5 py-0.5 rounded bg-indigo-500/15 text-indigo-200 border border-indigo-500/30"
                          title={`Hora real: ${localDate(f.timestamp)} · jornada iniciada el ${f.fecha}`}
                          data-testid={`cruza-${f.id}`}>🌙 cruza medianoche</span>
                      )}
                    </td>
                    <td className="p-2.5 font-mono text-zinc-200">{fmtTime(f.timestamp)}</td>
                    <td className="p-2.5">
                      <span className={`text-[10px] px-2 py-0.5 rounded ${f.tipo === "entrada" ? "bg-emerald-500/15 text-emerald-300" : "bg-red-500/15 text-red-300"}`}>
                        {f.tipo === "entrada" ? "↑ Entrada" : "↓ Salida"}
                      </span>
                      {f.edited_at && <span className="ml-1 text-[9px] text-amber-400" title={`Editado: ${f.edit_reason}`}>✏️</span>}
                    </td>
                    <td className="p-2.5 text-xs text-zinc-400 max-w-[200px] truncate">{f.trabajo_realizado || "—"}</td>
                    <td className="p-2.5 text-[10px] text-zinc-500">
                      {f.ip || "—"} {f.geo && <><br />{f.geo.lat?.toFixed(4)}, {f.geo.lng?.toFixed(4)}</>}
                    </td>
                    <td className="p-2.5 text-right">
                      {!isReadOnly && (
                        <>
                          <Button size="icon" variant="ghost" onClick={() => openEdit(f)}
                            className="h-7 w-7 text-zinc-400 hover:text-amber-300"
                            data-testid={`edit-${f.id}`}>
                            <Edit className="w-3.5 h-3.5" />
                          </Button>
                          <Button size="icon" variant="ghost" onClick={() => remove(f)}
                            className="h-7 w-7 text-zinc-400 hover:text-red-400"
                            data-testid={`del-${f.id}`}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      ) : tab === "resumen" ? (
        <ResumenMensualBlock
          resumen={resumen}
          mes={resumenMes}
          onMesChange={setResumenMes}
          onExport={exportResumenCSV}
          onReload={loadResumen}
          isReadOnly={isReadOnly}
        />
      ) : tab === "banco" ? (
        <BancoHorasBlock isReadOnly={isReadOnly} />
      ) : tab === "discrepancias" ? (
        <div className="space-y-4" data-testid="tab-discrepancias-content">
          {!discrepancias || discrepancias.total === 0 ? (
            <div className="bg-emerald-500/10 border border-emerald-500/30 rounded p-6 text-center">
              <Check className="w-8 h-8 text-emerald-400 mx-auto mb-2" />
              <div className="text-zinc-200">Sin discrepancias detectadas</div>
            </div>
          ) : (
            <>
              <DiscrepBlock title="Jornadas sin cerrar (entrada sin salida)" items={discrepancias.sin_cerrar} color="red" iconRender={(i) => `${i.entradas}↑ ${i.salidas}↓`} actionLabel="🔧 Cerrar manualmente" onAction={(item) => setRescatarEntrada(item)} />
              <DiscrepBlock title="Jornadas excesivas (>12h)" items={discrepancias.excesivas} color="amber" iconRender={(i) => `${i.horas}h`} />
              <DiscrepBlock title="Jornadas muy cortas (<1h)" items={discrepancias.cortas} color="zinc" iconRender={(i) => `${i.minutos}min`} />
            </>
          )}
        </div>
      ) : (
        <div className="space-y-2" data-testid="tab-correcciones-content">
          {correcciones.length === 0 ? (
            <div className="text-center py-12 text-zinc-500">Sin correcciones</div>
          ) : (
            correcciones.map((c) => (
              <div key={c.id} className={`bg-zinc-900/40 border rounded p-3 ${c.estado === "pendiente" ? "border-amber-500/40" : "border-zinc-800"}`}
                data-testid={`corr-${c.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-zinc-100">
                      {c.user_nombre} · <span className="text-zinc-400">{c.estado}</span>
                    </div>
                    <div className="text-xs text-zinc-400 mt-0.5">📝 {c.motivo}</div>
                    {c.timestamp_propuesto && (
                      <div className="text-xs text-zinc-500 mt-0.5">
                        Propuesto: {c.tipo_propuesto} {new Date(c.timestamp_propuesto).toLocaleString("es-ES")}
                        {c.nuevo_fichaje && " (NUEVO)"}
                      </div>
                    )}
                    {c.comentario_admin && (
                      <div className="text-xs text-zinc-400 mt-1 italic">↳ {c.comentario_admin} ({c.resuelta_by_nombre})</div>
                    )}
                  </div>
                  {c.estado === "pendiente" && (
                    <div className="flex gap-1.5 shrink-0">
                      <Button size="sm" onClick={() => resolverCorr(c, true)}
                        className="bg-emerald-500 text-zinc-950 hover:bg-emerald-400 h-7 text-xs"
                        data-testid={`corr-aprobar-${c.id}`}>
                        <Check className="w-3 h-3 mr-1" /> Aprobar
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => resolverCorr(c, false)}
                        className="bg-transparent border-red-700 text-red-300 hover:bg-red-500/10 h-7 text-xs"
                        data-testid={`corr-rechazar-${c.id}`}>
                        <X className="w-3 h-3 mr-1" /> Rechazar
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Edit dialog */}
      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100 max-w-md" data-testid="dialog-edit-fichaje">
          <DialogHeader>
            <DialogTitle>Editar fichaje</DialogTitle>
            <DialogDescription className="text-zinc-500 text-xs">
              La edición queda registrada en auditoría con motivo obligatorio.
            </DialogDescription>
          </DialogHeader>
          {edit && (
            <div className="space-y-3">
              <div className="text-xs text-zinc-400">Empleado: <b className="text-zinc-100">{edit.user_nombre}</b></div>
              <div>
                <Label className="text-xs">Fecha y hora</Label>
                <Input type="datetime-local" value={editForm.timestamp}
                  onChange={(e) => setEditForm({ ...editForm, timestamp: e.target.value })}
                  className="bg-zinc-900 border-zinc-800" data-testid="edit-timestamp" />
              </div>
              <div>
                <Label className="text-xs">Tipo</Label>
                <Select value={editForm.tipo} onValueChange={(v) => setEditForm({ ...editForm, tipo: v })}>
                  <SelectTrigger className="bg-zinc-900 border-zinc-800" data-testid="edit-tipo"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-zinc-950 border-zinc-800 text-zinc-100">
                    <SelectItem value="entrada">Entrada</SelectItem>
                    <SelectItem value="salida">Salida</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Motivo edición *</Label>
                <Textarea value={editForm.edit_reason}
                  onChange={(e) => setEditForm({ ...editForm, edit_reason: e.target.value })}
                  placeholder="Ej. Corrección a petición de Juan, olvido fichar"
                  className="bg-zinc-900 border-zinc-800 min-h-[60px]" data-testid="edit-reason" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEdit(null)}
              className="bg-transparent border-zinc-700 text-zinc-300">Cancelar</Button>
            <Button onClick={saveEdit} className="bg-amber-500 text-zinc-950 hover:bg-amber-400"
              data-testid="edit-save">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Iter 94 — Diálogo cerrar entrada huérfana */}
      <Dialog open={!!rescatarEntrada} onOpenChange={(o) => !o && setRescatarEntrada(null)}>
        <DialogContent className="bg-zinc-950 border-red-500/40 max-w-md" data-testid="rescate-dialog">
          <DialogHeader>
            <DialogTitle className="text-red-200 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" /> Cerrar jornada huérfana
            </DialogTitle>
            <DialogDescription className="text-xs text-zinc-400 pt-1">
              Crea una salida manual para una entrada que lleva &gt;24h sin cerrar.
              Útil cuando el empleado olvidó fichar o estaba fuera de cobertura.
            </DialogDescription>
          </DialogHeader>
          {rescatarEntrada && (
            <div className="space-y-3 text-sm">
              <div className="bg-zinc-900/60 border border-zinc-800 rounded p-2 text-xs">
                <div><span className="text-zinc-500">Empleado:</span> <b className="text-zinc-100">{rescatarEntrada.user_nombre}</b></div>
                <div><span className="text-zinc-500">Entrada del:</span> <span className="text-zinc-100">{rescatarEntrada.fecha}</span></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-zinc-400">Fecha salida *</Label>
                  <Input type="date" value={rescateForm.fecha}
                    onChange={(e) => setRescateForm({ ...rescateForm, fecha: e.target.value })}
                    className="bg-zinc-900 border-zinc-800 mt-1"
                    data-testid="rescate-fecha" />
                </div>
                <div>
                  <Label className="text-xs text-zinc-400">Hora salida *</Label>
                  <Input type="time" value={rescateForm.hora}
                    onChange={(e) => setRescateForm({ ...rescateForm, hora: e.target.value })}
                    className="bg-zinc-900 border-zinc-800 mt-1"
                    data-testid="rescate-hora" />
                </div>
              </div>
              <div>
                <Label className="text-xs text-zinc-400">Motivo (opcional)</Label>
                <Input value={rescateForm.motivo}
                  onChange={(e) => setRescateForm({ ...rescateForm, motivo: e.target.value })}
                  placeholder="Ej: Empleado de viaje fuera de cobertura"
                  className="bg-zinc-900 border-zinc-800 mt-1"
                  data-testid="rescate-motivo" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setRescatarEntrada(null)}>Cancelar</Button>
            <Button onClick={ejecutarRescate} disabled={rescatando}
              className="bg-red-500 hover:bg-red-400 text-zinc-950 font-semibold"
              data-testid="rescate-confirm">
              {rescatando ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Check className="w-3.5 h-3.5 mr-1.5" />}
              Cerrar jornada
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function DiscrepBlock({ title, items, color, iconRender, actionLabel, onAction }) {
  const [open, setOpen] = useState(true);
  if (!items?.length) return null;
  const cls = {
    red: "border-red-500/30 bg-red-500/5 text-red-300",
    amber: "border-amber-500/30 bg-amber-500/5 text-amber-300",
    zinc: "border-zinc-700 bg-zinc-900/40 text-zinc-300",
  }[color];
  return (
    <div className={`border rounded ${cls}`}>
      <button onClick={() => setOpen(!open)} className="w-full p-3 flex items-center justify-between text-sm font-medium">
        <span><AlertTriangle className="w-3.5 h-3.5 inline mr-2" />{title} ({items.length})</span>
        {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
      </button>
      {open && (
        <div className="border-t border-current/20 p-2 space-y-1">
          {items.map((i, idx) => (
            <div key={idx} className="flex items-center justify-between text-xs px-2 py-1">
              <span className="text-zinc-200">{i.user_nombre || "?"} · {i.fecha}</span>
              <div className="flex items-center gap-2">
                <span className="font-mono">{iconRender(i)}</span>
                {actionLabel && onAction && (
                  <Button size="sm" variant="outline"
                    onClick={(ev) => { ev.stopPropagation(); onAction(i); }}
                    data-testid={`discrep-action-${idx}`}
                    className="h-6 text-[10px] px-2 bg-red-500/10 border-red-500/40 text-red-200 hover:bg-red-500/20">
                    {actionLabel}
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function SemaforoBadge({ value }) {
  const map = {
    verde: { cls: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30", txt: "OK" },
    ambar: { cls: "bg-amber-500/15 text-amber-300 border-amber-500/30", txt: "Revisar" },
    rojo:  { cls: "bg-red-500/15 text-red-300 border-red-500/30", txt: "Alerta" },
  };
  const m = map[value] || map.rojo;
  return (
    <span className={`text-[10px] px-2 py-0.5 rounded border ${m.cls}`} data-testid={`sem-${value}`}>
      {m.txt}
    </span>
  );
}

function ResumenMensualBlock({ resumen, mes, onMesChange, onExport, onReload, isReadOnly }) {
  const now = new Date();
  const [cerrando, setCerrando] = useState(false);
  const periodo = `${mes.year}-${String(mes.month).padStart(2, "0")}`;
  const cerrarPeriodo = async (userId = null) => {
    if (!window.confirm(`¿Cerrar/recomputar el periodo ${periodo}${userId ? " para este empleado" : " para todos"}? Conserva pagos y compensaciones; sólo recalcula el saldo base.`)) return;
    setCerrando(true);
    try {
      await api.post("/rrhh/banco-horas/cerrar-periodo", { periodo, user_id: userId });
      toast.success(`Periodo ${periodo} cerrado/recomputado`);
      onReload && onReload();
    } catch (err) { toast.error(err?.response?.data?.detail || "Error cerrando periodo"); }
    finally { setCerrando(false); }
  };
  const yearOpts = [];
  for (let y = now.getFullYear() - 2; y <= now.getFullYear() + 1; y++) yearOpts.push(y);
  return (
    <div className="space-y-4" data-testid="tab-resumen-content">
      {/* Selector mes/año + export */}
      <div className="bg-zinc-900/40 border border-zinc-800/70 rounded-lg p-3 flex flex-wrap gap-3 items-end">
        <div>
          <Label className="text-xs text-zinc-400">Mes</Label>
          <Select value={String(mes.month)} onValueChange={(v) => onMesChange({ ...mes, month: parseInt(v, 10) })}>
            <SelectTrigger className="bg-zinc-950 border-zinc-800 w-40" data-testid="resumen-mes">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-zinc-950 border-zinc-800 text-zinc-100">
              {MESES_ES.map((m, i) => <SelectItem key={i + 1} value={String(i + 1)}>{m}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs text-zinc-400">Año</Label>
          <Select value={String(mes.year)} onValueChange={(v) => onMesChange({ ...mes, year: parseInt(v, 10) })}>
            <SelectTrigger className="bg-zinc-950 border-zinc-800 w-28" data-testid="resumen-year">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-zinc-950 border-zinc-800 text-zinc-100">
              {yearOpts.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="flex-1" />
        <Button onClick={onExport} className="bg-amber-500 text-zinc-950 hover:bg-amber-400" data-testid="resumen-export">
          <Download className="w-4 h-4 mr-2" /> Exportar resumen CSV
        </Button>
      </div>

      {!resumen ? (
        <div className="text-center py-12 text-zinc-500">Sin datos</div>
      ) : (
        <>
          {/* Totales */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="resumen-totales">
            <KPI label="Empleados" value={resumen.totales.empleados} />
            <KPI label="Días laborables" value={resumen.dias_laborables} />
            <KPI label="Horas trabajadas" value={resumen.totales.horas_trabajadas + " h"} />
            <KPI label="Horas extra" value={"+" + resumen.totales.horas_extra_positivas + " h"} color="emerald" icon={TrendingUp} />
            <KPI label="Déficit horas" value={"−" + resumen.totales.horas_deficit + " h"} color="red" icon={TrendingDown} />
          </div>
          {resumen.totales.con_horario_default > 0 && (
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 text-xs text-amber-200 flex items-center gap-2"
              data-testid="aviso-horario-default">
              <AlertTriangle className="w-4 h-4 shrink-0" />
              {resumen.totales.con_horario_default} empleado(s) sin horario configurado: sus horas esperadas usan el fallback de 8 h/día.
              Configura <b>horario inicio/fin</b> y <b>pausa</b> en Usuarios para un cómputo fiable.
            </div>
          )}

          {/* Tabla por empleado */}
          <div className="bg-zinc-900/30 border border-zinc-800/60 rounded-lg overflow-x-auto" data-testid="resumen-tabla">
            <table className="w-full text-sm">
              <thead className="bg-zinc-950/60 text-[10px] uppercase tracking-wider text-zinc-500 border-b border-zinc-800">
                <tr>
                  <th className="text-left p-2.5">Empleado</th>
                  <th className="text-left p-2.5">Horario</th>
                  <th className="text-right p-2.5">Esperadas</th>
                  <th className="text-right p-2.5">Trabajadas</th>
                  <th className="text-right p-2.5">Extra / Déficit</th>
                  <th className="text-right p-2.5">Días fich.</th>
                  <th className="text-right p-2.5" title="Jornadas que cruzan medianoche">🌙 Noct.</th>
                  <th className="text-right p-2.5">Disc.</th>
                  <th className="text-right p-2.5">Cumpl.</th>
                  <th className="text-left p-2.5">Estado</th>
                </tr>
              </thead>
              <tbody>
                {resumen.empleados.length === 0 ? (
                  <tr><td colSpan={10} className="text-center py-8 text-zinc-500">Sin empleados obligados a fichar</td></tr>
                ) : resumen.empleados.map((e) => (
                  <tr key={e.user_id} className="border-b border-zinc-800/60 hover:bg-zinc-900/40"
                    data-testid={`resumen-row-${e.user_id}`}>
                    <td className="p-2.5 text-zinc-100">{e.user_nombre}</td>
                    <td className="p-2.5 text-zinc-400 text-xs font-mono">
                      {e.horario_inicio || "--"} - {e.horario_fin || "--"}
                      {e.pausa_min > 0 && <span className="text-zinc-500"> · pausa {e.pausa_min}'</span>}
                      {e.fuente_horario === "default" && (
                        <span className="ml-1.5 text-[9px] px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-300 border border-amber-500/30"
                          title="Sin horario configurado: se asumen 8 h/día" data-testid={`default-${e.user_id}`}>default 8h</span>
                      )}
                    </td>
                    <td className="p-2.5 text-right text-zinc-300 font-mono">{e.horas_esperadas.toFixed(1)} h</td>
                    <td className="p-2.5 text-right text-zinc-100 font-mono">{e.horas_trabajadas.toFixed(1)} h</td>
                    <td className={`p-2.5 text-right font-mono font-medium ${
                      e.horas_extra > 0 ? "text-emerald-300" : e.horas_extra < 0 ? "text-red-300" : "text-zinc-400"
                    }`} data-testid={`extra-${e.user_id}`}>
                      {e.horas_extra > 0 ? "+" : ""}{e.horas_extra.toFixed(1)} h
                    </td>
                    <td className="p-2.5 text-right text-zinc-300 font-mono">{e.dias_fichados}/{e.dias_laborables}</td>
                    <td className={`p-2.5 text-right font-mono ${e.jornadas_nocturnas > 0 ? "text-indigo-200" : "text-zinc-600"}`}
                      data-testid={`noct-${e.user_id}`}>{e.jornadas_nocturnas || 0}</td>
                    <td className={`p-2.5 text-right font-mono ${e.dias_discrepancia > 0 ? "text-amber-300" : "text-zinc-500"}`}>
                      {e.dias_discrepancia}
                    </td>
                    <td className="p-2.5 text-right text-zinc-200 font-mono">{e.cumplimiento_pct.toFixed(0)}%</td>
                    <td className="p-2.5"><SemaforoBadge value={e.semaforo} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="text-[10px] text-zinc-500 px-1">
            Semáforo: <span className="text-emerald-300">OK</span> ≥95% · <span className="text-amber-300">Revisar</span> 75-95% · <span className="text-red-300">Alerta</span> &lt;75%.
            Cómputo por <b>fecha laboral</b> (Europe/Madrid): horario real − pausa, festivos y ausencias aprobadas = 0 h esperadas, fines de semana excluidos.
            Las jornadas que cruzan medianoche se agrupan en el día de la entrada.
          </div>

          {/* Banco de horas · saldo del periodo */}
          <div className="bg-zinc-900/30 border border-zinc-800/60 rounded-lg overflow-x-auto" data-testid="banco-periodo-panel">
            <div className="px-4 py-2 text-xs uppercase tracking-wider text-zinc-500 border-b border-zinc-800 flex items-center gap-2">
              <Wallet className="w-3.5 h-3.5" /> Saldo mensual · {periodo}
              <span className="flex-1" />
              {!isReadOnly && (
                <Button size="sm" variant="outline" disabled={cerrando} onClick={() => cerrarPeriodo(null)}
                  className="h-7 text-[11px] bg-transparent border-zinc-700 text-zinc-300" data-testid="cerrar-periodo-todos">
                  {cerrando ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Check className="w-3 h-3 mr-1" />}
                  Cerrar / recomputar periodo
                </Button>
              )}
            </div>
            <table className="w-full text-sm">
              <thead className="bg-zinc-950/60 text-[10px] uppercase tracking-wider text-zinc-500 border-b border-zinc-800">
                <tr>
                  <th className="text-left p-2.5">Empleado</th>
                  <th className="text-right p-2.5">Debía</th>
                  <th className="text-right p-2.5">Trabajó</th>
                  <th className="text-right p-2.5">Saldo</th>
                  <th className="text-right p-2.5">Compensado</th>
                  <th className="text-right p-2.5">Pagado</th>
                  <th className="text-right p-2.5">Ajustes</th>
                  <th className="text-right p-2.5">Pendiente</th>
                  <th className="text-left p-2.5">Cierre</th>
                </tr>
              </thead>
              <tbody>
                {resumen.empleados.map((e) => {
                  const b = e.banco || {};
                  return (
                    <tr key={e.user_id} className="border-b border-zinc-800/60 hover:bg-zinc-900/40" data-testid={`banco-row-${e.user_id}`}>
                      <td className="p-2.5 text-zinc-100">{e.user_nombre}</td>
                      <td className="p-2.5 text-right font-mono text-zinc-300">{(b.esperadas ?? 0).toFixed(1)} h</td>
                      <td className="p-2.5 text-right font-mono text-zinc-100">{(b.trabajadas ?? 0).toFixed(1)} h</td>
                      <td className={`p-2.5 text-right font-mono ${b.saldo > 0 ? "text-emerald-300" : b.saldo < 0 ? "text-red-300" : "text-zinc-400"}`}>{fmtH(b.saldo)}</td>
                      <td className="p-2.5 text-right font-mono text-amber-300">{(b.compensado ?? 0).toFixed(1)}</td>
                      <td className="p-2.5 text-right font-mono text-violet-300">{(b.pagado ?? 0).toFixed(1)}</td>
                      <td className="p-2.5 text-right font-mono text-zinc-400">{fmtH((b.ajustes ?? 0) + (b.recuperaciones ?? 0))}</td>
                      <td className={`p-2.5 text-right font-mono font-semibold ${b.pendiente > 0 ? "text-emerald-300" : b.pendiente < 0 ? "text-red-300" : "text-zinc-400"}`}
                        data-testid={`pendiente-${e.user_id}`}>{fmtH(b.pendiente)}</td>
                      <td className="p-2.5 text-xs">
                        <span className={`text-[10px] px-2 py-0.5 rounded border ${b.estado === "cerrado"
                          ? "bg-emerald-500/10 text-emerald-300 border-emerald-500/30"
                          : "bg-zinc-800 text-zinc-400 border-zinc-700"}`}>
                          {b.estado === "cerrado" ? "cerrado" : "provisional"}
                        </span>
                        {b.needs_recompute && <span className="ml-1 text-[9px] text-amber-300">recomputar</span>}
                        {!isReadOnly && (
                          <button onClick={() => cerrarPeriodo(e.user_id)} disabled={cerrando}
                            className="ml-2 text-[10px] text-zinc-400 hover:text-amber-300 underline"
                            data-testid={`cerrar-periodo-${e.user_id}`}>
                            {b.estado === "cerrado" ? "recomputar" : "cerrar"}
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <div className="text-[10px] text-zinc-500 px-4 py-2">
              Pendiente = saldo + ajustes + recuperaciones − compensado − pagado. Los pagos/compensaciones se conservan al recomputar
              tras corregir fichajes. Los movimientos históricos sin periodo se muestran como <i>legacy</i> en la pestaña Banco.
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function KPI({ label, value, color, icon: Icon }) {
  const txt = {
    emerald: "text-emerald-300",
    red: "text-red-300",
  }[color] || "text-zinc-100";
  return (
    <div className="bg-zinc-900/40 border border-zinc-800/60 rounded-lg p-3">
      <div className="text-[10px] uppercase tracking-wider text-zinc-500 flex items-center gap-1">
        {Icon && <Icon className="w-3 h-3" />} {label}
      </div>
      <div className={`text-xl font-semibold mt-1 ${txt}`}>{value}</div>
    </div>
  );
}


const TIPO_MOV_COLOR = {
  compensacion: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  recuperacion: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  pago: "bg-violet-500/15 text-violet-300 border-violet-500/30",
  ajuste: "bg-zinc-700/40 text-zinc-300 border-zinc-600",
};
const TIPO_MOV_LABEL = {
  compensacion: "Compensación",
  recuperacion: "Recuperación",
  pago: "Pago",
  ajuste: "Ajuste",
};

function BancoHorasBlock({ isReadOnly }) {
  const [saldos, setSaldos] = useState([]);
  const [movs, setMovs] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialog, setDialog] = useState(false);
  const emptyMov = () => ({
    user_id: "", fecha: new Date().toISOString().slice(0, 10),
    tipo: "compensacion", horas: -2, motivo: "", autorizado_por_nombre: "",
    aplica_a: new Date().toISOString().slice(0, 7),
  });
  const [form, setForm] = useState(emptyMov());

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, m, u] = await Promise.all([
        api.get("/rrhh/banco-horas/saldos"),
        api.get("/rrhh/banco-horas"),
        api.get("/users"),
      ]);
      setSaldos(s.data || []);
      setMovs(m.data || []);
      setUsers(u.data || []);
    } catch { toast.error("Error cargando banco horas"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (!form.user_id) return toast.error("Selecciona empleado");
    if (!form.fecha) return toast.error("Indica fecha");
    if (!form.horas || form.horas === 0) return toast.error("Horas ≠ 0");
    try {
      await api.post("/rrhh/banco-horas", form);
      toast.success("Movimiento añadido");
      setDialog(false);
      setForm(emptyMov());
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
    }
  };

  const remove = async (m) => {
    if (!window.confirm(`Eliminar movimiento de ${m.horas}h?`)) return;
    try {
      await api.delete(`/rrhh/banco-horas/${m.id}`);
      toast.success("Eliminado");
      load();
    } catch { toast.error("Error eliminando"); }
  };

  if (loading) return <div className="text-zinc-500 text-center py-12"><Loader2 className="inline w-5 h-5 animate-spin mr-2" />Cargando…</div>;

  return (
    <div className="space-y-4" data-testid="tab-banco-content">
      <div className="flex justify-between items-center gap-3 flex-wrap">
        <div className="text-xs text-zinc-500 max-w-[600px]">
          <b>Saldo</b> = pendiente total (banco 2.0: cierres mensuales + movimientos con periodo). Fuente de verdad = fichajes/correcciones;
          <i>recuperación</i> bloqueada en periodos cerrados (corrige el fichaje); <i>ajuste</i> requiere motivo. Movimientos sin periodo = <i>legacy</i>.
        </div>
        {!isReadOnly && (
          <Button onClick={() => setDialog(true)} className="bg-amber-500 text-zinc-950 hover:bg-amber-400" data-testid="banco-add">
            <Plus className="w-4 h-4 mr-2" /> Añadir movimiento
          </Button>
        )}
      </div>

      <div className="bg-zinc-900/30 border border-zinc-800/60 rounded-lg overflow-hidden" data-testid="banco-saldos">
        <div className="px-4 py-2 text-xs uppercase tracking-wider text-zinc-500 border-b border-zinc-800 flex items-center gap-2">
          <Wallet className="w-3.5 h-3.5" /> Saldo por empleado
        </div>
        <table className="w-full text-sm">
          <thead className="bg-zinc-950/60 text-[10px] uppercase tracking-wider text-zinc-500 border-b border-zinc-800">
            <tr>
              <th className="text-left p-2.5">Empleado</th>
              <th className="text-right p-2.5">Pendiente</th>
              <th className="text-right p-2.5">Periodos cerrados</th>
              <th className="text-right p-2.5">Recuperaciones</th>
              <th className="text-right p-2.5">Compensaciones</th>
              <th className="text-right p-2.5">Pagos</th>
              <th className="text-right p-2.5">Ajustes</th>
              <th className="text-right p-2.5" title="Movimientos históricos sin periodo asignado">Legacy</th>
            </tr>
          </thead>
          <tbody>
            {saldos.length === 0 ? (
              <tr><td colSpan={8} className="text-center py-6 text-zinc-500">Sin empleados</td></tr>
            ) : saldos.map((s) => (
              <tr key={s.user_id} className="border-b border-zinc-800/60 hover:bg-zinc-900/40" data-testid={`banco-saldo-${s.user_id}`}>
                <td className="p-2.5 text-zinc-100">{s.user_nombre}</td>
                <td className={`p-2.5 text-right font-mono font-semibold ${s.saldo > 0 ? "text-emerald-300" : s.saldo < 0 ? "text-red-300" : "text-zinc-400"}`}>
                  {s.saldo > 0 ? "+" : ""}{s.saldo.toFixed(1)} h
                </td>
                <td className="p-2.5 text-right text-zinc-400 font-mono">{s.periodos_cerrados ?? 0}</td>
                <td className="p-2.5 text-right text-emerald-300 font-mono">{s.recuperaciones > 0 ? "+" : ""}{s.recuperaciones.toFixed(1)}</td>
                <td className="p-2.5 text-right text-amber-300 font-mono">{s.compensaciones.toFixed(1)}</td>
                <td className="p-2.5 text-right text-violet-300 font-mono">{s.pagos.toFixed(1)}</td>
                <td className="p-2.5 text-right text-zinc-400 font-mono">{s.ajustes > 0 ? "+" : ""}{s.ajustes.toFixed(1)}</td>
                <td className="p-2.5 text-right text-zinc-500 font-mono" title={`Saldo histórico legacy: ${(s.saldo_legacy ?? 0).toFixed(1)} h`}>
                  {s.legacy_movimientos || 0}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="bg-zinc-900/30 border border-zinc-800/60 rounded-lg overflow-hidden" data-testid="banco-movimientos">
        <div className="px-4 py-2 text-xs uppercase tracking-wider text-zinc-500 border-b border-zinc-800">
          Movimientos ({movs.length})
        </div>
        {movs.length === 0 ? (
          <div className="text-center py-8 text-zinc-500">Sin movimientos. Pulsa "Añadir movimiento" para registrar.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-zinc-950/60 text-[10px] uppercase tracking-wider text-zinc-500 border-b border-zinc-800">
              <tr>
                <th className="text-left p-2.5">Fecha</th>
                <th className="text-left p-2.5">Empleado</th>
                <th className="text-left p-2.5">Tipo</th>
                <th className="text-left p-2.5">Periodo</th>
                <th className="text-right p-2.5">Horas</th>
                <th className="text-left p-2.5">Motivo</th>
                <th className="text-left p-2.5">Autoriza</th>
                <th className="p-2.5"></th>
              </tr>
            </thead>
            <tbody>
              {movs.map((m) => (
                <tr key={m.id} className="border-b border-zinc-800/60 hover:bg-zinc-900/40" data-testid={`mov-${m.id}`}>
                  <td className="p-2.5 font-mono text-xs text-zinc-300">{m.fecha}</td>
                  <td className="p-2.5 text-zinc-100">{m.user_nombre}</td>
                  <td className="p-2.5">
                    <span className={`text-[10px] px-2 py-0.5 rounded border ${TIPO_MOV_COLOR[m.tipo] || TIPO_MOV_COLOR.ajuste}`}>
                      {TIPO_MOV_LABEL[m.tipo] || m.tipo}
                    </span>
                  </td>
                  <td className="p-2.5 text-xs font-mono">
                    {m.aplica_a ? <span className="text-zinc-300">{m.aplica_a}</span>
                      : <span className="text-[9px] px-1.5 py-0.5 rounded bg-zinc-800 text-zinc-400 border border-zinc-700" title="Movimiento histórico sin periodo asignado (no entra en ningún cierre)" data-testid={`legacy-${m.id}`}>legacy · no asignado</span>}
                  </td>
                  <td className={`p-2.5 text-right font-mono font-semibold ${m.horas > 0 ? "text-emerald-300" : "text-red-300"}`}>
                    {m.horas > 0 ? "+" : ""}{m.horas.toFixed(1)} h
                  </td>
                  <td className="p-2.5 text-xs text-zinc-400 max-w-[200px] truncate">{m.motivo || "—"}</td>
                  <td className="p-2.5 text-xs text-zinc-500">{m.autorizado_por_nombre || m.created_by_nombre || "—"}</td>
                  <td className="p-2.5 text-right">
                    {!isReadOnly && (
                      <Button size="sm" variant="ghost" onClick={() => remove(m)}
                        className="h-7 w-7 text-red-400 hover:bg-red-500/10"
                        data-testid={`mov-del-${m.id}`}>
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100 max-w-md" data-testid="dialog-banco">
          <DialogHeader>
            <DialogTitle>Añadir movimiento al banco</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Empleado</Label>
              <Select value={form.user_id} onValueChange={(v) => setForm({ ...form, user_id: v })}>
                <SelectTrigger className="bg-zinc-900 border-zinc-800" data-testid="banco-user"><SelectValue placeholder="Selecciona…" /></SelectTrigger>
                <SelectContent className="bg-zinc-950 border-zinc-800 text-zinc-100 max-h-72">
                  {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">Fecha</Label>
                <Input type="date" value={form.fecha} onChange={(e) => setForm({ ...form, fecha: e.target.value })}
                  className="bg-zinc-900 border-zinc-800" data-testid="banco-fecha" />
              </div>
              <div>
                <Label className="text-xs">Tipo</Label>
                <Select value={form.tipo} onValueChange={(v) => {
                  const abs = Math.abs(form.horas) || 1;
                  const horas = (v === "recuperacion") ? abs : (v === "ajuste") ? form.horas : -abs;
                  setForm({ ...form, tipo: v, horas });
                }}>
                  <SelectTrigger className="bg-zinc-900 border-zinc-800" data-testid="banco-tipo"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-zinc-950 border-zinc-800 text-zinc-100">
                    <SelectItem value="compensacion">Compensación (sale antes / −)</SelectItem>
                    <SelectItem value="recuperacion">Recuperación (suma horas / +)</SelectItem>
                    <SelectItem value="pago">Pago en nómina (−)</SelectItem>
                    <SelectItem value="ajuste">Ajuste manual (+/−)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-xs">Periodo al que aplica (YYYY-MM)</Label>
              <Input type="month" value={form.aplica_a} onChange={(e) => setForm({ ...form, aplica_a: e.target.value })}
                className="bg-zinc-900 border-zinc-800" data-testid="banco-aplica-a" />
              {form.tipo === "recuperacion" && (
                <div className="text-[10px] text-amber-300 mt-1">Sólo para horas NO fichadas y en periodos abiertos. Si son horas trabajadas, corrige el fichaje.</div>
              )}
              {form.tipo === "ajuste" && (
                <div className="text-[10px] text-zinc-400 mt-1">Vía excepcional: motivo obligatorio y trazable.</div>
              )}
            </div>
            <div>
              <Label className="text-xs">Horas (positivo suma, negativo resta)</Label>
              <Input type="number" step="0.5" value={form.horas}
                onChange={(e) => setForm({ ...form, horas: parseFloat(e.target.value) || 0 })}
                className="bg-zinc-900 border-zinc-800" data-testid="banco-horas-input" />
            </div>
            <div>
              <Label className="text-xs">Motivo</Label>
              <Textarea value={form.motivo} onChange={(e) => setForm({ ...form, motivo: e.target.value })}
                placeholder="Ej: Salió a las 12:00 con permiso de Dani"
                className="bg-zinc-900 border-zinc-800 min-h-[60px]" data-testid="banco-motivo" />
            </div>
            <div>
              <Label className="text-xs">Quién autoriza</Label>
              <Input value={form.autorizado_por_nombre}
                onChange={(e) => setForm({ ...form, autorizado_por_nombre: e.target.value })}
                placeholder="Dani / Sara / Fernando"
                className="bg-zinc-900 border-zinc-800" data-testid="banco-autoriza" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(false)} className="bg-transparent border-zinc-700 text-zinc-300">Cancelar</Button>
            <Button onClick={submit} className="bg-amber-500 text-zinc-950 hover:bg-amber-400" data-testid="banco-submit">
              <Check className="w-4 h-4 mr-2" /> Añadir
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

